import { AvatarGroup, IconButton, Modal, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import axios from '../util/Axios';
import { useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import CommentIcon from '@mui/icons-material/Comment';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import TestDetails from '../components/TestDetails/TestDetails';
import { FilterSelect } from '../components/FilterSelect';
import { AddToDataSet } from '../components/AddToDataSet';
import { DataSetCodeList } from './Datasets/DataSetCodeList';
import { useLocation } from 'react-router-dom';

import { StatusAvatar } from '../components/TestStatuses/StatusAvatar';

function affectedStatus(test) {
  const neurologicalExamination = test.neurologicalExamination;
  if (!neurologicalExamination) {
    return 'Invalid';
  }
  const examinationStatus = neurologicalExamination.ischemic || neurologicalExamination.hemorrhagic ? 'Affected' : 'Healthy';

  return examinationStatus;
}

const filterToolbarStyle = { display: 'flex', justifyContent: 'flex-end' };

function statusFilterParam(statusFilter) {
  return statusFilter.length > 0 ? `${`&status=${statusFilter}`}` : '';
}

function hashTagFilterParam(hashTagFilter) {
  return hashTagFilter.length > 0 ? `${`&hashTag=${encodeURIComponent(hashTagFilter)}`}` : '';
}

export default function AllTests() {
  const [hashTags, setHashTags] = useState([]);
  useEffect(() => {
    axios(`/hashTags`).then((response) => {
      setHashTags(response.data.map((s) => ({ name: s, value: s })));
    });
  }, []);

  const [hashTagFilter, setHashTagFilter] = useState('');

  const handleChangeHashTagFilter = (event) => {
    const {
      target: { value },
    } = event;
    setHashTagFilter(value);
  };

  const [statusFilter, setStatusFilter] = useState(['VALID', 'UNKNOWN', 'MAYBE', 'DUPLICATE']);

  const handleChangeFilter = (event) => {
    const {
      target: { value },
    } = event;
    setStatusFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const [tests, setTests] = useState([]);

  const [shouldRefresh, setShouldRefresh] = useState(false);

  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [pageParam, setPageParam] = useState(0);

  const [page, setPage] = useState({ number: 0, count: 0 });

  const location = useLocation();

  useEffect(() => {
    axios(`/strokeTests/details${location.pathname}?${hashTagFilterParam(hashTagFilter)}${statusFilterParam(statusFilter)}&sort=doctorId,userId,createdAt&size=${rowsPerPage}&page=${pageParam}`).then((response) => {
      const tests = response.data.content;
      setTests(tests);
      setPage({ number: response.data.number, count: response.data.totalElements });
      setShouldRefresh(false);
    });
  }, [rowsPerPage, pageParam, shouldRefresh, statusFilter, hashTagFilter, location.pathname]);

  const [openVideo, setOpenVideo] = useState(false);
  const [openedTestId, setOpenedTestId] = useState(null);

  const [selectedTestId, setSelectedTestId] = useState(null);

  const handleOpenDetails = (testId) => {
    setOpenedTestId(testId);
    setOpenVideo(true);
    setSelectedTestId(testId);
  };

  const handleCloseVideo = () => {
    setOpenVideo(false);
    setOpenedTestId(null);
    setShouldRefresh(true);
  };

  const [openAddToSet, setOpenAddToSet] = useState(false);

  const handleOpenAddToSet = (testId) => {
    setOpenedTestId(testId);
    setOpenAddToSet(true);
  };

  const handleCloseAddToSet = () => {
    setOpenedTestId(null);
    setOpenAddToSet(false);
    setShouldRefresh(true);
  };

  return (
    <>
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          fontSize: '2em',
          width: '100%',
        }}
      >
        All Tests
      </Typography>
      <Typography
        variant="subtitle1"
        component="body"
        sx={{
          textAlign: 'center',
          width: '100%',
        }}
      ></Typography>
      <div style={filterToolbarStyle}>
        <FilterSelect filter={hashTagFilter} label="Filter by #" handleChangeFilter={handleChangeHashTagFilter} items={hashTags} />
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </div>
      <Table size="small" aria-label="purchases">
        <TableHead>
          <TableRow>
            <TableCell>Doctor</TableCell>
            <TableCell>Patient</TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Examination Status</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Comment</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tests.map((test) => (
            <TableRow key={test.uuid} sx={test.uuid === selectedTestId ? { backgroundColor: '#e3f2fd' } : {}}>
              <TableCell>{test.doctor?.name}</TableCell>
              <TableCell>{test.userId.substr(0, 8)}</TableCell>
              <TableCell>{test.uuid.substr(0, 8)}</TableCell>
              <TableCell>{affectedStatus(test)}</TableCell>
              <TableCell>
                <AvatarGroup>
                  <StatusAvatar status={test.faceStatus} type="face" />
                  <StatusAvatar status={test.speechStatus} type="speech" />
                  <StatusAvatar status={test.armsStatus} type="arms" />
                  <StatusAvatar status={test.neurologicalExaminationStatus} type="neurologicalExamination" />
                </AvatarGroup>
              </TableCell>
              <TableCell>
                {test.comment ? (
                  <Tooltip title={test.comment}>
                    <IconButton>
                      <CommentIcon></CommentIcon>
                    </IconButton>
                  </Tooltip>
                ) : (
                  ''
                )}
              </TableCell>
              <TableCell>{test.createdAt.split('.')[0].replace('T', ' ')}</TableCell>
              <TableCell>
                <IconButton onClick={() => handleOpenDetails(test.uuid)}>
                  <Tooltip title="View details">
                    <InfoIcon />
                  </Tooltip>
                </IconButton>
                <IconButton onClick={() => handleOpenAddToSet(test.uuid)}>
                  <Tooltip title="Add to data set">
                    <AddIcon />
                  </Tooltip>
                </IconButton>
                <DataSetCodeList dataSetIds={test.dataSets?.map((d) => d.dataSetId)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100, 1000]} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => setRowsPerPage(e.target.value)} count={page.count} page={page.number} onPageChange={(e, newPage) => setPageParam(newPage)} />
          </TableRow>
        </TableFooter>
      </Table>
      <Modal open={openVideo} onClose={handleCloseVideo}>
        <TestDetails id={openedTestId} onClose={handleCloseVideo}></TestDetails>
      </Modal>
      <AddToDataSet testId={openedTestId} open={openAddToSet} handleClose={handleCloseAddToSet} />
    </>
  );
}
